import React, { useEffect, useRef, useState } from 'react';

// Self-serve white-label logo for the Talent Intelligence report. Stores to the
// existing teams.logo_url / users.logo_url via /api/monitoring/report-branding.
// Team admins set the team logo; solo users set their own.

const MAX_BYTES = 600 * 1024;

const ReportBrandingSettings: React.FC = () => {
  const [logoUrl, setLogoUrl] = useState<string | null>(null);
  const [canEdit, setCanEdit] = useState(true);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const fileRef = useRef<HTMLInputElement>(null);
  const token = localStorage.getItem('token');

  useEffect(() => {
    fetch('/api/monitoring/report-branding', { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => { if (d) { setLogoUrl(d.logoUrl || null); setCanEdit(d.canEdit !== false); } })
      .catch(() => {})
      .finally(() => setLoading(false));
    // eslint-disable-next-line
  }, []);

  const save = async (value: string | null) => {
    setSaving(true); setError(null); setSaved(false);
    try {
      const res = await fetch('/api/monitoring/report-branding', {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ logoUrl: value }),
      });
      const d = await res.json();
      if (!res.ok) throw new Error(d.error || 'Failed to save');
      setLogoUrl(d.logoUrl || null);
      setSaved(true);
      setTimeout(() => setSaved(false), 2500);
    } catch (e: any) {
      setError(e?.message || 'Failed to save');
    } finally { setSaving(false); }
  };

  const onFile = (e: React.ChangeEvent<HTMLInputElement>) => {
    const f = e.target.files && e.target.files[0];
    if (!f) return;
    if (!/^image\/(png|jpe?g|svg\+xml|webp)$/.test(f.type)) { setError('Please choose a PNG, JPG, SVG or WEBP image.'); return; }
    if (f.size > MAX_BYTES) { setError('Image is too large (max 600KB). Try a smaller PNG or an SVG.'); return; }
    const reader = new FileReader();
    reader.onload = () => save(String(reader.result));
    reader.readAsDataURL(f);
  };

  return (
    <div className="max-w-md mb-10 pb-8 border-b border-gray-100">
      <h3 className="text-lg font-medium mb-2">Report Branding</h3>
      <p className="text-sm text-gray-500 mb-4">
        Add your own logo to the Talent Intelligence report so you can share it as your own.
        Falls back to the MarketMapr mark when none is set.
      </p>
      {loading ? (
        <p className="text-sm text-gray-400">Loading…</p>
      ) : (
        <>
          <div className="w-44 h-16 rounded-lg border border-gray-200 bg-gray-900 flex items-center justify-center overflow-hidden mb-3">
            {logoUrl
              ? <img src={logoUrl} alt="report logo" className="max-h-12 max-w-[150px] object-contain" />
              : <span className="text-gray-500 text-xs">No logo — MarketMapr default</span>}
          </div>
          {canEdit ? (
            <div className="flex items-center gap-2 flex-wrap">
              <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/webp" onChange={onFile} className="hidden" />
              <button onClick={() => fileRef.current && fileRef.current.click()} disabled={saving}
                className="px-3 py-1.5 text-sm bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50">
                {saving ? 'Uploading…' : (logoUrl ? 'Replace logo' : 'Upload logo')}
              </button>
              {logoUrl && (
                <button onClick={() => save(null)} disabled={saving}
                  className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50">Remove</button>
              )}
              {saved && <span className="text-green-600 text-sm">Saved ✓</span>}
            </div>
          ) : (
            <p className="text-sm text-gray-400">Only your team admin can change the report logo.</p>
          )}
          {error && <p className="text-red-600 text-sm mt-2">{error}</p>}
          <p className="text-[11px] text-gray-400 mt-2">Best with a white or transparent PNG/SVG (it sits on a dark header). Max 600KB.</p>
        </>
      )}
    </div>
  );
};

export default ReportBrandingSettings;
